import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, DestroyRef, inject, Injector, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';
import { ClipboardService } from 'ngx-clipboard';
import { TooltipModule } from 'primeng/tooltip';
import * as dot from 'ts-dot-prop';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { FormValidationStatus } from '../../../@core/types/form-validation-status.type';
import { IBaseModel } from '../../../generated/generated-types';

// Base interface for icon definition (with functions for dynamic behavior)
export interface IconDefinition {
  iconClass: string;
  href?: string;
  target?: string;
  className?: string;
  tooltip?: string;
  clickHandler?: (originalIcon: IconDefinition, event: Event) => void;
  condition?: (model: IBaseModel) => boolean;
  dynamicIconClass?: (model: IBaseModel) => string;
  dynamicTooltip?: (model: IBaseModel) => string;
}

// Resolved interface for template consumption (no functions, pure data)
export interface ResolvedIcon {
  iconClass: string;
  href?: string;
  target?: string;
  className?: string;
  tooltip: string;
  clickHandler?: (originalIcon: IconDefinition, event: Event) => void;
  originalIcon: IconDefinition;
}

@Component({
  selector: 'formly-simple-title-wrapper',
  templateUrl: './simple-title-wrapper.component.html',
  styleUrls: ['./simple-title-wrapper.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    FormlyModule,
  ]
})
export class SimpleTitleWrapperComponent extends FieldWrapper implements AfterViewInit {

  protected readonly i18nService = inject(I18nService);

  dataTestId = input<string>();

  protected readonly injector = inject(Injector);

  private destroyRef = inject(DestroyRef);

  // === CORE STATE SIGNALS ===
  protected readonly modelSignal = signal<IBaseModel>(null);
  protected readonly formValueSignal = signal<any>(null);
  protected readonly propsSignal = signal<any>({});

  // === DERIVED SIGNALS FOR ICONS ===

  // Extract only model data that affects icons (override in subclasses)
  protected readonly iconRelevantData = computed(() => {
    const model = this.modelSignal(); //when model changes
    const formValue = this.formValueSignal(); //when the formcontrol changes we want to trigger the effect

    // Don't proceed if model is null, undefined, or just an empty object
    if (!model || (typeof model === 'object' && Object.keys(model).length === 0)) {
      return null;
    }

    return {
      id: model.id,
      fieldKey: this.field?.key,
      ...this.extractIconRelevantData(model)
    };
  });

  // Pre-icons (left side) - resolved and ready for template
  readonly preIcons = computed(() => {
    const relevantData = this.iconRelevantData(); // Triggers on relevant changes only
    const model = this.modelSignal();

    // Don't show icons if we don't have valid model data
    if (!relevantData || !model || (typeof model === 'object' && Object.keys(model).length === 0)) {
      // console.info("no model data", relevantData, model);
      return [];
    }

    const definitions = this.getPreIconDefinitions();
    const filtered = definitions.filter(icon => this.evaluateCondition(icon, model));
    const resolved = filtered.map(icon => this.resolveIcon(icon, model));

    return resolved;
  });

  // Post-icons (right side) - resolved and ready for template
  readonly postIcons = computed(() => {
    const relevantData = this.iconRelevantData(); // Triggers on relevant changes only
    const model = this.modelSignal();

    // Don't show icons if we don't have valid model data
    if (!relevantData || !model || (typeof model === 'object' && Object.keys(model).length === 0)) {
      return [];
    }

    return this.getPostIconDefinitions()
      .filter(icon => this.evaluateCondition(icon, model))
      .map(icon => this.resolveIcon(icon, model));
  });

  readonly labelClasses = computed(() => {
    const formValue = this.formValueSignal();
    const props = this.propsSignal();

    const classes: Record<string, boolean> = {
      'label-with-icons': !!(formValue && (props?.editBasePath || props?.showCopyIcon))
    };

    if (props?.labelClassName) {
      classes[props.labelClassName] = true;
    }

    if (props?.hrBelowTitle) {
      classes['h6'] = true;
      classes['label-with-hr'] = true;
      classes['text-primary'] = true;
      classes['font-semibold'] = true;
    }

    return classes;
  });

  // Computed valid ID from model - reactive to form value changes
  readonly validIdOfModel = computed(() => {
    const value = this.modelSignal();
    return value?.id || value;
  });

  // === TRACKBY FUNCTIONS ===
  readonly trackByPreIcon = (index: number, icon: ResolvedIcon): string =>
    `pre-${icon.iconClass}-${icon.tooltip}-${index}`;

  readonly trackByPostIcon = (index: number, icon: ResolvedIcon): string =>
    `post-${icon.iconClass}-${icon.tooltip}-${index}`;

  constructor(
    private clipboardService: ClipboardService,
    private notificationService: NotificationService,
  ) {
    super();
  }

  /**
   * Translated `props.hint`, or null when the field has none.
   * A hint is either an already translated sentence or a translation key.
   * Keys have no spaces. Sentences are left as they are, so a second translate pass cannot rewrite them.
   */
  get hintText(): string | null {
    const hint = this.props?.hint as string | null | undefined;
    if (!hint) {
      return null;
    }
    if (hint.includes(' ')) {
      return hint;
    }
    return this.i18nService.translate(hint) || hint;
  }

  ngAfterViewInit() {
    this.initializeSignals();
    this.setupFormControlSubscription();
  }

  private setupFormControlSubscription(): void {
    //if we dont have a key, valueChanges is triggered by every model change
    if (this.formControl && this.key) {
      this.formControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          // console.info("formValueSignal", this.key, value);
          this.formValueSignal.set(value);

          // When form control changes, also check if model has been updated
          this.checkAndUpdateModel();
        });
    }
    else {
      // console.warn("no formControl or key", this.key);
    }

    // Listen to form status changes to detect when model is fully set
    if (this.form) {
      this.form.statusChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(status => {
          if (status === FormValidationStatus.VALID || status === FormValidationStatus.INVALID) {
            this.checkModelAfterFormReady();
          }
        });
    }
  }

  private checkModelAfterFormReady(): void {
    if (this.model && Object.keys(this.model).length > 0 && this.key) {
      const modelValue = dot.get(this.model, this.key as string);
      const formValue = this.formControl?.value;

      // If model has value but formControl doesn't reflect it yet
      // THEY SHOULD NEVER BE THE SAME
      // if (modelValue !== formValue) {
      this.formValueSignal.set(modelValue);
      this.modelSignal.set(this.model);
      // }
    }
  }

  private initializeSignals(): void {
    // Only set model if we have actual data, not an empty object
    if (this.model && typeof this.model === 'object' && Object.keys(this.model).length > 0) {
      this.modelSignal.set(this.model);
    } else {
      this.modelSignal.set(null);
    }

    this.propsSignal.set(this.props || {});
    this.formValueSignal.set(this.formControl?.value || null);
  }

  // === ICON SYSTEM (OVERRIDE IN SUBCLASSES) ===

  // Override this to extract only the model properties that affect your icons
  protected extractIconRelevantData(model: any): Record<string, any> {
    return {}; // Base implementation returns empty object
  }

  // Override this to define pre-icons (left side)
  protected getPreIconDefinitions(): IconDefinition[] {
    return []; // Base implementation returns no icons
  }

  // Override this to define post-icons (right side)  
  protected getPostIconDefinitions(): IconDefinition[] {
    return this.props?.customIcons || []; // Base implementation uses props
  }

  // Override this to provide custom pre-template content
  protected generatePreTemplateContent(): string {
    return ''; // Base implementation returns empty string
  }

  // === ICON RESOLUTION (INTERNAL) ===

  private evaluateCondition(icon: IconDefinition, model: IBaseModel): boolean {
    // console.info("evaluateCondition", icon, model);
    return !icon.condition || icon.condition(model);
  }

  private resolveIcon(icon: IconDefinition, model: IBaseModel): ResolvedIcon {
    return {
      iconClass: icon.dynamicIconClass ? icon.dynamicIconClass(model) : icon.iconClass,
      href: icon.href,
      target: icon.target,
      className: icon.className,
      tooltip: icon.dynamicTooltip ? icon.dynamicTooltip(model) : (icon.tooltip || ''),
      clickHandler: icon.clickHandler,
      originalIcon: icon
    };
  }

  // === TEMPLATE HELPERS ===

  copyToClipboard(text: string) {
    this.clipboardService.copyFromContent(text);
    this.notificationService.success(_("common.copied-to-clipboard"), { entity: "company" });
  }

  /**
   * Manually refresh icons by updating the model signal to trigger recomputation
   * This is useful when the model has been updated externally and icons need to reflect changes
   */
  refreshIcons(): void {
    const currentModel = this.modelSignal();
    if (currentModel) {
      // Force signal update by setting the same object with a new reference
      // This ensures computed signals that depend on the model will recalculate
      this.modelSignal.set({ ...currentModel });
    }
  }

  handleCustomIconClick(resolvedIcon: ResolvedIcon, event: Event): void {
    const icon = resolvedIcon.originalIcon;
    if (icon.clickHandler) {
      icon.clickHandler(icon, event);
    } else if (icon.href && icon.href !== 'javascript:void(0)') {
      return; // Let browser handle navigation
    }
    // Prevent default for javascript:void(0) links
    if (!icon.href || icon.href === 'javascript:void(0)') {
      event.preventDefault();
    }
  }

  protected checkAndUpdateModel(): void {
    if (this.model && typeof this.model === 'object' && Object.keys(this.model).length > 0) {
      const currentModel = this.modelSignal();
      if (!currentModel || Object.keys(currentModel).length === 0) {
        this.modelSignal.set(this.model);
      }
    }
  }
}
